import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import UpdateCompanyMutation from '../../../../mutations/update_company_mutation.modern';
import {applyToggleChange} from './exportDefaults';
import {ExportDefaultsCard} from './ExportDefaultsCard';
import {
	QBO_EXCLUSIVE_PAIRS,
	QBO_EXPORT_DEFAULT_TOGGLES,
	QboExportDefaultField,
	QboExportDefaults,
	StoredQboExportDefaults,
	toQboExportDefaults,
} from './qboExportDefaults';

type UpdateCompanyResponse = {updateCompany?: {errors?: ReadonlyArray<string | null> | null} | null};

type Props = {
	companyId: string;
	stored: StoredQboExportDefaults;
};

export const QuickbooksExportDefaultsSection = ({companyId, stored}: Props) => {
	const {formatMessage} = useIntl();
	const [values, setValues] = useState<QboExportDefaults>(() => toQboExportDefaults(stored));
	const [saving, setSaving] = useState(false);

	// After a save, Relay updates the store and new props arrive: show what the server stored.
	useEffect(() => {
		setValues(toQboExportDefaults(stored));
	}, [
		stored.qboExportDefaultInvoiceRef,
		stored.qboExportDefaultDescription,
		stored.qboExportDefaultProjectNameInDesc,
		stored.qboExportDefaultTerms,
		stored.qboExportDefaultTax,
	]);

	const onToggle = (field: QboExportDefaultField, checked: boolean) =>
		setValues(current => applyToggleChange(current, field, checked, QBO_EXCLUSIVE_PAIRS));

	const onSave = () => {
		setSaving(true);
		Util.CommitMutation(
			UpdateCompanyMutation,
			{id: companyId, ...values},
			(response: UpdateCompanyResponse) => {
				setSaving(false);
				// A server refusal (e.g. INVALID_QBO_EXPORT_DEFAULTS) currently surfaces as a transport
				// error and gets Util.CommitMutation's default error modal, so it does not land here.
				// This check is a defensive guard for payload-style refusals: a resolved payload that
				// carries `errors` must never be announced as saved.
				if (response?.updateCompany?.errors?.length) {
					showModal({type: MODAL_TYPE.WARNING, warningMessageId: 'common.invalid_action_modal_title'});
				} else {
					createToast({duration: 10000, message: formatMessage({id: 'common.settings_saved'})});
				}
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean | void) => {
				// onFailure returns true when it has scheduled a retry; keep the spinner in that case.
				if (onFailure(transaction) !== true) setSaving(false);
			}
		);
	};

	return (
		<ExportDefaultsCard
			titleId="integrations.quickbooks.export_defaults.title"
			introId="integrations.quickbooks.export_defaults.intro"
			saveId="common.save"
			toggles={QBO_EXPORT_DEFAULT_TOGGLES}
			values={values}
			onToggle={onToggle}
			onSave={onSave}
			saving={saving}
			cyPrefix="qbo-export-defaults"
		/>
	);
};
